/**
 * Landing page for the confirm and unsubscribe links in notice emails
 * (policy BUILD-10 §2).
 *
 * The emails contain ordinary links, so the browser arrives with a GET. The state
 * change itself is a POST, which this page makes on arrival — a link that a mail
 * client or a link-scanner prefetches must not be able to confirm a subscription
 * by being fetched, and a GET that mutates would allow exactly that.
 *
 * Unsubscribing, by contrast, is safe to perform on arrival: it only ever removes
 * consent, and making the person click a second button to stop receiving mail is
 * hostile. Confirmation requires the explicit click.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  confirmSubprocessorSubscription,
  unsubscribeFromSubprocessorNotices,
} from '@/api/trust'

type Mode = 'confirm' | 'unsubscribe'
type State = 'idle' | 'working' | 'done' | 'error'

export function TrustNoticeActionPage({ mode }: { mode: Mode }) {
  const [params] = useSearchParams()
  const token = params.get('token') || ''
  const id = params.get('id') || ''

  const [state, setState] = useState<State>('idle')
  const [message, setMessage] = useState('')

  const run = useCallback(async () => {
    setState('working')
    try {
      if (mode === 'confirm') {
        await confirmSubprocessorSubscription(token)
        setMessage('You are subscribed. We will email you before any new subprocessor starts handling your data.')
      } else {
        await unsubscribeFromSubprocessorNotices(id, token)
        setMessage('You have been unsubscribed. We will not send you further subprocessor notices.')
      }
      setState('done')
    } catch (err) {
      setState('error')
      setMessage(
        (err as Error).message
          || 'That link could not be used. It may have expired, or already been used.',
      )
    }
  }, [mode, token, id])

  // Unsubscribe is idempotent and only ever withdraws consent, so it runs on
  // arrival. Confirmation waits for a deliberate click.
  useEffect(() => {
    if (mode === 'unsubscribe' && token && id) void run()
  }, [mode, token, id, run])

  const missing = mode === 'confirm' ? !token : !token || !id

  return (
    <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6 lg:px-8">
      <h1 className="mb-4 text-2xl font-bold tracking-tight">
        {mode === 'confirm' ? 'Confirm your subprocessor notices' : 'Unsubscribe from subprocessor notices'}
      </h1>

      {missing ? (
        <p role="alert" className="text-muted-foreground">
          This link is incomplete. Please use the link exactly as it appears in the email, or start
          again from the{' '}
          <Link to="/trust#subprocessor-notices" className="underline underline-offset-4">
            Trust Centre
          </Link>
          .
        </p>
      ) : null}

      {!missing && mode === 'confirm' && state === 'idle' ? (
        <>
          <p className="mb-4 text-muted-foreground">
            Click below to confirm that you want to receive notices when we add a subprocessor.
          </p>
          <button
            type="button"
            onClick={() => void run()}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            Confirm subscription
          </button>
        </>
      ) : null}

      {state === 'working' ? <p className="text-muted-foreground">Working…</p> : null}

      {state === 'done' || state === 'error' ? (
        <p
          role={state === 'error' ? 'alert' : 'status'}
          aria-live="polite"
          className="text-muted-foreground"
        >
          {message}
        </p>
      ) : null}

      <p className="mt-8 text-sm text-muted-foreground">
        <Link to="/trust" className="underline underline-offset-4">Back to the Trust Centre</Link>
      </p>
    </div>
  )
}

export default TrustNoticeActionPage
